import { QRCodeCtaInput } from '@/engagements/base/admin/QRCodeCtaInput';
import { TitleInput } from '@/engagements/base/admin/TitleInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  TriviaAdminConfig,
  TriviaAdminData,
  TriviaQuestion,
  TriviaRoundStatus,
} from '@/generated/graphql';
import {
  configChanged,
  descriptionChanged,
  editorModeChanged,
} from '@/providers/AdminEngagementProvider/actions';
import { useAdminEngagementContext } from '@/providers/AdminEngagementProvider/AdminEngagementContext';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DotsVerticalIcon,
  PlusIcon,
} from '@radix-ui/react-icons';
import { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { cn } from '@/lib/utils';
import {
  TRIVIA_CHOICE_MAX_CHARS,
  TRIVIA_MAX_CHOICES,
  TRIVIA_MAX_TIMER_S,
  TRIVIA_MIN_CHOICES,
  TRIVIA_MIN_TIMER_S,
  TRIVIA_QUESTION_MAX_CHARS,
  TRIVIA_TIMER_PRESETS_S,
} from '../constants';
import { LetterTile } from '../LetterTile';
import { CheckIcon } from '../TriviaIcons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { generatePath } from 'react-router-dom';
import { FAN_ROUTE, STAGE_ROUTE } from '@/routes/consts';
import { EditorModeSwitcher } from '@/routes/admin/screen/editor/EditorModeSwitcher';
import { saveStatusLabel } from './saveStatus';
import {
  collectQuestionErrors,
  errorsForQuestion,
  parseTriviaSaveError,
  QuestionFieldError,
} from './questionValidation';
import {
  applyCustomTimerSeconds,
  applyQuestionTimerMode,
  customTimerHint,
  questionTimerMode,
  timerSecondsForInput,
} from './questionTimer';
import { triviaLockedQuestionCount } from './playedLock';
import './trivia-editor.css';

function blankChoice() {
  return { id: uuid(), text: '' };
}

function blankQuestion(): TriviaQuestion {
  return {
    id: uuid(),
    text: '',
    timerMs: null,
    correctChoiceId: null,
    choices: [blankChoice(), blankChoice()],
  };
}

function CharCount({ value, max }: { value: string; max: number }) {
  const n = value.length;
  return (
    <span
      className={cn(
        'text-xs tabular-nums text-muted-foreground',
        n >= max * 0.8 && 'font-semibold text-advisory',
      )}
    >
      {n}/{max}
    </span>
  );
}

function fieldClass(invalid?: boolean) {
  return cn(
    'bg-[#1a1614] text-foreground',
    invalid && 'border-destructive focus-visible:ring-destructive',
  );
}

export function TriviaEditorView() {
  const { state, dispatch, event, saveStatus } = useAdminEngagementContext();
  const config = state.config as TriviaAdminConfig;
  const data = state.data as TriviaAdminData;
  const questions = useMemo(() => config.questions ?? [], [config.questions]);
  const [openId, setOpenId] = useState<string | null>(questions[0]?.id ?? null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const roundStatus = data?.status;
  const roundComplete = roundStatus === TriviaRoundStatus.RoundComplete;
  const roundInProgress = Boolean(
    roundStatus && roundStatus !== TriviaRoundStatus.Idle && !roundComplete,
  );
  const lockedCount = triviaLockedQuestionCount(data ?? {});

  const localErrors = useMemo(
    () => collectQuestionErrors(questions),
    [questions],
  );
  const serverError =
    saveStatus.kind === 'error'
      ? parseTriviaSaveError(saveStatus.message)
      : null;
  const allErrors: QuestionFieldError[] = serverError
    ? [
        serverError,
        ...localErrors.filter(
          (error) =>
            error.index !== serverError.index ||
            error.field !== serverError.field,
        ),
      ]
    : localErrors;

  const setConfig = (patch: Partial<TriviaAdminConfig>) => {
    dispatch(configChanged({ config: patch }));
  };

  const setQuestions = (next: TriviaQuestion[]) => {
    setConfig({ questions: next });
  };

  const instructions = config.instructions ?? state.description ?? '';
  const stageSrc = `${generatePath(STAGE_ROUTE, { id: event.id })}?engagementId=${state.id}`;
  const mobileSrc = `${generatePath(FAN_ROUTE, { id: event.id })}?engagementId=${state.id}`;

  return (
    <div className="trivia-admin-editor flex min-w-0 flex-col gap-4">
      <div className="trivia-preview-thumb min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <button
            type="button"
            className="trivia-chip rounded-md px-2 py-1 text-xs"
            onClick={() => setPreviewOpen((open) => !open)}
          >
            {previewOpen ? 'Hide preview' : 'Stage preview'}
          </button>
          <p
            className={cn(
              'text-sm font-medium',
              saveStatus.kind === 'error' ? 'text-red-400' : 'text-neutral-400',
            )}
            data-testid="trivia-save-status"
            aria-live="polite"
          >
            {saveStatusLabel(saveStatus)}
          </p>
          {previewOpen ? (
            <EditorModeSwitcher
              value={state.editorMode}
              onChange={(editorMode) =>
                dispatch(editorModeChanged({ editorMode }))
              }
            />
          ) : null}
        </div>
        {previewOpen ? (
          <div className="trivia-preview-frame mt-2">
            <iframe
              title="Trivia preview"
              src={state.editorMode === 'mobile' ? mobileSrc : stageSrc}
            />
          </div>
        ) : null}
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(240px,280px)] lg:items-start">
        <section className="min-w-0 rounded-xl border border-white/10 bg-[#120e0c] p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-bold">
              Questions
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-semibold">
                {questions.length}
              </span>
            </h3>
            {questions.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Drag to reorder. Players see them in this order.
              </p>
            ) : null}
          </div>
          {roundInProgress ? (
            <div className="mb-3 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-neutral-300">
              Round in progress. You can edit questions that haven&apos;t been
              played yet.
            </div>
          ) : null}
          {roundComplete ? (
            <div className="mb-3 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-neutral-300">
              Round complete.
            </div>
          ) : null}
          {questions.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/15 p-8 text-center">
              <p className="font-semibold">No questions yet</p>
              <p className="text-sm text-muted-foreground">
                Add your first question to get going. You need at least one
                question to start a round.
              </p>
              <Button
                onClick={() => {
                  const q = blankQuestion();
                  setQuestions([q]);
                  setOpenId(q.id);
                }}
              >
                + Add question
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  open={openId === question.id}
                  locked={index < lockedCount}
                  useTimer={config.useQuestionTimer !== false}
                  defaultTimerMs={config.defaultTimerMs ?? 30000}
                  total={questions.length}
                  fieldErrors={errorsForQuestion(allErrors, index)}
                  onToggle={() =>
                    setOpenId((id) => (id === question.id ? null : question.id))
                  }
                  onMove={(dir) => {
                    if (index < lockedCount || index + dir < lockedCount)
                      return;
                    const next = [...questions];
                    const swap = index + dir;
                    if (swap < 0 || swap >= next.length) return;
                    [next[index], next[swap]] = [next[swap], next[index]];
                    setQuestions(next);
                  }}
                  onChange={(nextQ) => {
                    setQuestions(
                      questions.map((q) => (q.id === nextQ.id ? nextQ : q)),
                    );
                  }}
                  onDuplicate={() => {
                    const copy: TriviaQuestion = {
                      ...question,
                      id: uuid(),
                      choices: question.choices.map((c) => ({
                        ...c,
                        id: uuid(),
                      })),
                      correctChoiceId: null,
                    };
                    const next = [...questions];
                    next.splice(index + 1, 0, copy);
                    setQuestions(next);
                    setOpenId(copy.id);
                  }}
                  onDelete={() => {
                    setQuestions(questions.filter((q) => q.id !== question.id));
                    if (openId === question.id) setOpenId(null);
                  }}
                />
              ))}
              <Button
                variant="outline"
                className="mt-2 border-white/15 bg-[#1a1614] text-foreground"
                onClick={() => {
                  const q = blankQuestion();
                  setQuestions([...questions, q]);
                  setOpenId(q.id);
                }}
              >
                <PlusIcon className="mr-1 h-4 w-4" />
                Add question
              </Button>
            </div>
          )}
        </section>

        <aside className="flex min-w-0 flex-col gap-3">
          <section className="rounded-xl border border-white/10 bg-[#120e0c] p-4">
            <h3 className="mb-3 text-sm font-bold">Details</h3>
            <label className="mb-1 block text-sm font-medium" htmlFor="title">
              Title
            </label>
            <TitleInput />
            <label
              className="mb-1 mt-4 block text-sm font-medium"
              htmlFor="trivia-instructions"
            >
              Instructions{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <Textarea
              id="trivia-instructions"
              className={fieldClass()}
              value={instructions}
              onChange={(e) => {
                const value = e.target.value;
                setConfig({ instructions: value });
                dispatch(descriptionChanged({ description: value }));
              }}
              placeholder="e.g. Answer on your phone. +1 point for every right answer."
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shows on the Stage and phones before the first question.
            </p>
            <label
              className="mb-1 mt-4 block text-sm font-medium"
              htmlFor="qr-code-cta"
            >
              QR code label
            </label>
            <QRCodeCtaInput />
          </section>
          <section className="rounded-xl border border-white/10 bg-[#120e0c] p-4">
            <h3 className="mb-3 text-sm font-bold">Timer</h3>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Use question timer</p>
                <p className="text-xs text-muted-foreground">
                  Answers close on their own when time&apos;s up.
                </p>
              </div>
              <Switch
                checked={config.useQuestionTimer !== false}
                onCheckedChange={(checked) =>
                  setConfig({ useQuestionTimer: checked })
                }
              />
            </div>
            {config.useQuestionTimer !== false ? (
              <div className="mt-4">
                <p className="mb-2 text-sm font-medium">
                  Default time per question
                </p>
                <div className="trivia-chip-row">
                  {TRIVIA_TIMER_PRESETS_S.map((seconds) => {
                    const on =
                      (config.defaultTimerMs ?? 30000) === seconds * 1000;
                    return (
                      <button
                        key={seconds}
                        type="button"
                        className={cn('trivia-chip', on && 'is-on')}
                        onClick={() =>
                          setConfig({ defaultTimerMs: seconds * 1000 })
                        }
                      >
                        {seconds}s
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  You can change it for any single question.
                </p>
              </div>
            ) : null}
          </section>
          <section className="rounded-xl border border-white/10 bg-[#120e0c] p-4">
            <h3 className="mb-2 text-sm font-bold">Scoring</h3>
            <p className="text-sm text-muted-foreground">
              +1 point for each right answer. No speed bonus. Players can&apos;t
              change an answer once it&apos;s in.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  index,
  open,
  locked,
  useTimer,
  defaultTimerMs,
  total,
  fieldErrors,
  onToggle,
  onMove,
  onChange,
  onDuplicate,
  onDelete,
}: {
  question: TriviaQuestion;
  index: number;
  open: boolean;
  locked: boolean;
  useTimer: boolean;
  defaultTimerMs: number;
  total: number;
  fieldErrors: Partial<
    Record<'text' | 'choices' | 'correct' | 'timer', string>
  >;
  onToggle: () => void;
  onMove: (dir: -1 | 1) => void;
  onChange: (q: TriviaQuestion) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const filled = question.choices.filter((c) => c.text.trim());
  const missingCorrect = !question.correctChoiceId;
  const timerS = timerSecondsForInput(question, defaultTimerMs);
  const custom = questionTimerMode(question) === 'custom';
  const invalid = Object.keys(fieldErrors).length > 0;

  if (!open) {
    return (
      <div className="trivia-q-collapsed">
        <span className="cursor-grab text-neutral-500">⋮⋮</span>
        <button
          type="button"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-secondary text-xs font-bold text-foreground"
          onClick={onToggle}
        >
          {index + 1}
        </button>
        <button type="button" className="trivia-q-title" onClick={onToggle}>
          {question.text || 'Untitled question'}
        </button>
        <span className="hidden shrink-0 whitespace-nowrap text-xs text-neutral-400 sm:inline">
          {filled.length} choices · {timerS}s
        </span>
        {invalid ? (
          <span className="text-[11px] font-semibold text-advisory">
            Needs attention
          </span>
        ) : null}
        {locked ? (
          <span className="trivia-played-label text-[11px] text-muted-foreground">
            <span className="trivia-played-full">Already played</span>
            <span className="trivia-played-short">Played</span>
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="iconSm"
          className="hidden text-foreground sm:inline-flex"
          onClick={() => onMove(-1)}
        >
          <ChevronUpIcon />
        </Button>
        <Button
          variant="ghost"
          size="iconSm"
          className="hidden text-foreground sm:inline-flex"
          onClick={() => onMove(1)}
        >
          <ChevronDownIcon />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="iconSm" className="text-foreground">
              <DotsVerticalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={onDuplicate} disabled={locked}>
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} disabled={locked || total < 1}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className={cn('trivia-q-open', invalid && 'is-invalid')}>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="flex items-center gap-2 bg-transparent p-0 text-sm font-semibold text-foreground"
          onClick={onToggle}
        >
          <span
            className={cn(
              'grid h-6 w-6 place-items-center rounded-full text-xs font-bold',
              invalid ? 'bg-destructive text-white' : 'bg-[#FF6B3D] text-black',
            )}
          >
            {index + 1}
          </span>
          Question {index + 1}
        </button>
        <div className="flex items-center gap-2">
          {locked ? (
            <span className="trivia-played-label text-xs text-muted-foreground">
              <span className="trivia-played-full">Already played</span>
              <span className="trivia-played-short">Played</span>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
          <Button variant="ghost" size="iconSm" onClick={onToggle}>
            <ChevronDownIcon />
          </Button>
        </div>
      </div>
      <label className="flex items-center justify-between text-sm font-medium">
        <span>Question</span>
        <CharCount value={question.text} max={TRIVIA_QUESTION_MAX_CHARS} />
      </label>
      <Textarea
        value={question.text}
        maxLength={TRIVIA_QUESTION_MAX_CHARS}
        disabled={locked}
        className={fieldClass(Boolean(fieldErrors.text))}
        onChange={(e) => onChange({ ...question, text: e.target.value })}
      />
      {fieldErrors.text ? (
        <p className="text-xs text-destructive">{fieldErrors.text}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Up to {TRIVIA_QUESTION_MAX_CHARS} characters, so it stays big on the
          Stage.
        </p>
      )}
      <div className="flex items-center justify-between gap-3 text-sm font-medium">
        <span>Choices</span>
        <span className="trivia-choice-count font-normal text-neutral-400">
          {question.choices.length} of {TRIVIA_MAX_CHOICES}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        Select the right answer, up to {TRIVIA_CHOICE_MAX_CHARS} characters
        each.
      </p>
      {question.choices.map((choice, choiceIndex) => {
        const isCorrect = question.correctChoiceId === choice.id;
        return (
          <div
            key={choice.id}
            className={cn(
              'trivia-choice-row',
              missingCorrect && 'is-missing',
              isCorrect && '[&_input.trivia-choice-input]:border-[#50DC78]/55',
            )}
          >
            <input
              type="radio"
              name={`correct-${question.id}`}
              checked={isCorrect}
              disabled={locked}
              onChange={() =>
                onChange({ ...question, correctChoiceId: choice.id })
              }
              aria-label={`Mark ${choice.text || `choice ${choiceIndex + 1}`} correct`}
            />
            <LetterTile index={choiceIndex} />
            <Input
              className={cn(
                'trivia-choice-input',
                fieldClass(Boolean(fieldErrors.choices)),
              )}
              value={choice.text}
              maxLength={TRIVIA_CHOICE_MAX_CHARS}
              disabled={locked}
              onChange={(e) => {
                const choices = question.choices.map((c) =>
                  c.id === choice.id ? { ...c, text: e.target.value } : c,
                );
                onChange({ ...question, choices });
              }}
            />
            <span className="hidden sm:inline">
              <CharCount value={choice.text} max={TRIVIA_CHOICE_MAX_CHARS} />
            </span>
            {isCorrect ? (
              <span className="flex shrink-0 items-center gap-1 rounded-md bg-[#50DC78]/15 px-1.5 py-0.5 text-[11px] font-bold text-[#50DC78]">
                <CheckIcon size={12} />
                <span className="trivia-correct-label">Correct</span>
              </span>
            ) : null}
            <Button
              variant="ghost"
              size="iconSm"
              className="relative z-0 shrink-0 text-muted-foreground"
              disabled={locked || question.choices.length <= TRIVIA_MIN_CHOICES}
              aria-label="Remove choice"
              onClick={() => {
                const choices = question.choices.filter(
                  (c) => c.id !== choice.id,
                );
                onChange({
                  ...question,
                  choices,
                  correctChoiceId:
                    question.correctChoiceId === choice.id
                      ? null
                      : question.correctChoiceId,
                });
              }}
            >
              ×
            </Button>
          </div>
        );
      })}
      {fieldErrors.choices ? (
        <p className="text-xs text-destructive">{fieldErrors.choices}</p>
      ) : null}
      {question.choices.length < TRIVIA_MAX_CHOICES && !locked ? (
        <Button
          variant="outline"
          size="sm"
          className="w-fit border-white/15 bg-transparent text-[#FF6B3D]"
          onClick={() =>
            onChange({
              ...question,
              choices: [...question.choices, blankChoice()],
            })
          }
        >
          + Add choice
        </Button>
      ) : null}
      {missingCorrect ? (
        <p className="text-xs text-advisory">
          Pick the right answer before saving.
        </p>
      ) : null}
      {fieldErrors.correct && !missingCorrect ? (
        <p className="text-xs text-destructive">{fieldErrors.correct}</p>
      ) : null}
      {useTimer ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium">Time limit</span>
          <div className="trivia-chip-row">
            <button
              type="button"
              disabled={locked}
              className={cn('trivia-chip', !custom && 'is-on')}
              onClick={() =>
                onChange(
                  applyQuestionTimerMode(question, 'default', defaultTimerMs),
                )
              }
            >
              Default ({Math.round(defaultTimerMs / 1000)}s)
            </button>
            <button
              type="button"
              disabled={locked}
              className={cn('trivia-chip', custom && 'is-on')}
              onClick={() =>
                onChange(
                  applyQuestionTimerMode(question, 'custom', defaultTimerMs),
                )
              }
            >
              Custom
            </button>
          </div>
          {custom ? (
            <div className="flex min-w-0 flex-col gap-1">
              <Input
                type="number"
                className={cn('w-24', fieldClass(Boolean(fieldErrors.timer)))}
                min={TRIVIA_MIN_TIMER_S}
                max={TRIVIA_MAX_TIMER_S}
                disabled={locked}
                value={Number.isFinite(timerS) ? timerS : ''}
                onChange={(e) =>
                  onChange(
                    applyCustomTimerSeconds(question, Number(e.target.value)),
                  )
                }
              />
              <span className="text-xs text-muted-foreground">
                {customTimerHint()}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {fieldErrors.timer ? (
        <p className="text-xs text-destructive">{fieldErrors.timer}</p>
      ) : null}
    </div>
  );
}
